// frontend/client/src/AnonymousFeedPage.js
import React, { useEffect, useState } from "react";
import "./AnonymousFeedPage.css";

function AnonymousFeedPage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState("");
  const [text, setText] = useState("");

  // LOAD FEED
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/api/anonymous-feed", { credentials: "include" });
        const data = await res.json();
        if (!data.ok) throw new Error();

        setUser(data.user);
        setCsrfToken(data.csrfToken);
        setPosts(data.posts);
      } catch {
        setError("Failed to load feed.");
      }
      setLoading(false);
    }
    load();
  }, []);

  // SUBMIT POST
  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;

    try {
      const res = await fetch("/api/anonymous-feed", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: trimmed, csrfToken }),
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || "Failed.");
        return;
      }
      setText("");

      // reload feed
      const again = await fetch("/api/anonymous-feed", { credentials: "include" });
      const againData = await again.json();
      if (againData.ok) {
        setPosts(againData.posts);
        setCsrfToken(againData.csrfToken);
      }
    } catch {
      setError("Post error");
    }
  }

  // LIKE
  async function toggleLike(id) {
    try {
      const form = new URLSearchParams();
      form.append("postId", id);
      form.append("csrfToken", csrfToken);
      form.append("returnTo", "/anonymous-feed");

      const res = await fetch("/like-anonymous-post", {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        credentials: "include",
        body: form.toString(),
      });

      const data = await res.json();
      if (!data.ok) return;

      setPosts((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, likedByMe: data.liked, likeCount: data.likeCount }
            : p
        )
      );
    } catch {}
  }

  // COMMENT
  async function addComment(id, commentText) {
    const trimmed = commentText.trim();
    if (!trimmed) return;

    try {
      const form = new URLSearchParams();
      form.append("postId", id);
      form.append("comment", trimmed);
      form.append("csrfToken", csrfToken);
      form.append("returnTo", "/anonymous-feed");

      const res = await fetch("/comment-anonymous-post", {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        credentials: "include",
        body: form.toString(),
      });

      const data = await res.json();
      if (!data.ok) return;

      setPosts((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                comments: [...p.comments, { text: data.text || trimmed }],
                commentCount:
                  typeof data.commentCount === "number"
                    ? data.commentCount
                    : p.commentCount + 1,
              }
            : p
        )
      );
    } catch {}
  }

  // DELETE
  async function deletePost(id) {
    if (!window.confirm("Delete post?")) return;

    try {
      const form = new URLSearchParams();
      form.append("id", id);
      form.append("csrfToken", csrfToken);

      await fetch("/delete-anonymous-post", {
        method: "POST",
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: form.toString(),
      });

      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch {}
  }

  if (loading) return <div className="af-container">Loading...</div>;
  if (!user) return <div className="af-container">Login required.</div>;

  return (
    <div className="af-container">
      {/* === TOP BAR === */}
      <header className="af-topbar">
        <div style={{ fontWeight: 700, fontSize: 18 }}>GRADA · Anonymous feed</div>

        <button className="af-profile-btn" onClick={() => (window.location.href = "/profile")}>
          <img
            src="/avatar"
            className="af-profile-avatar"
            alt="Avatar"
            onError={(e) => (e.target.style.display = "none")}
          />
          <div>
            <div className="af-profile-name">{user.name}</div>
            <div className="af-profile-sub">{user.university}</div>
          </div>
        </button>
      </header>

      {/* === SHARE BOX === */}
      <section className="af-card">
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Share something anonymously</h2>

        {error && <div className="af-error-text">{error}</div>}

        <form onSubmit={handleSubmit}>
          <textarea
            className="af-textarea"
            placeholder="What's on your mind?"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          <button className="af-btn" type="submit">
            Post anonymously
          </button>

          <button
            type="button"
            className="af-btn af-btn-secondary"
            onClick={() => (window.location.href = "/dashboard")}
          >
            Back to main page
          </button>
        </form>
      </section>

      {/* === POSTS === */}
      <section>
        {posts.length === 0 ? (
          <div style={{ fontSize: 13, color: "#9ca3af" }}>
            No anonymous posts yet. Be the first 🎭
          </div>
        ) : (
          posts.map((p) => (
            <PostItem
              key={p.id}
              post={p}
              user={user}
              toggleLike={toggleLike}
              addComment={addComment}
              deletePost={deletePost}
            />
          ))
        )}
      </section>
    </div>
  );
}

function PostItem({ post, user, toggleLike, addComment, deletePost }) {
  const [commentText, setCommentText] = useState("");
  const createdLabel = (() => {
    if (!post.createdAt) return "";
    const d = new Date(post.createdAt);
    if (Number.isNaN(d.getTime())) return String(post.createdAt);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
  })();

  return (
    <div className="af-post">
      <div className="af-post-header">
        <div className="af-post-meta">
          Anonymous · {post.university} · {createdLabel}
        </div>

        {(post.isOwnPost || user.isAdmin) && (
          <button className="af-delete-btn" onClick={() => deletePost(post.id)}>
            Delete
          </button>
        )}
      </div>

      <p className="af-post-text">{post.text}</p>

      <div className="af-post-actions">
        <button className="af-like-btn" onClick={() => toggleLike(post.id)}>
          {post.likedByMe ? "Unlike" : "Like"} ({post.likeCount})
        </button>
        <div className="af-comment-count">{post.commentCount} comments</div>
      </div>

      <div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addComment(post.id, commentText);
            setCommentText("");
          }}
        >
          <input
            className="af-comment-input"
            type="text"
            placeholder="Write a comment anonymously..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
        </form>

        {post.comments.map((c, i) => (
          <div key={i} className="af-comment-row">
            <span className="af-comment-author">Anon:</span>
            <span>{c.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AnonymousFeedPage;
